/**
 * User Management Service
 * 
 * Provides comprehensive user management functionality for managers including:
 * - User CRUD operations
 * - Role and permission management
 * - User activity tracking
 * - Performance metrics
 * - Bulk operations
 * - Security and audit logging
 * 
 * @module UserManagementService
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  QueryConstraint,
  Timestamp,
  writeBatch,
  DocumentSnapshot,
  serverTimestamp
} from 'firebase/firestore';
import { initializeApp, getApp, getApps } from 'firebase/app';
import { StoreAssignmentService } from './StoreAssignmentService';
import { 
  createUserWithEmailAndPassword,
  updateProfile,
  sendPasswordResetEmail,
  getAuth,
  User as FirebaseUser,
  deleteUser
} from 'firebase/auth';
import { db, auth, firebaseConfig } from '../config/firebase';
import { ENV } from '../config/env';
import {
  UserProfile,
  CreateUserData,
  UpdateUserData,
  UserFilters,
  UserSort,
  UserActionResult,
  BulkUserAction,
  UserStatistics,
  UserActivity,
  UserPerformanceMetrics,
  UserRole,
  UserStatus,
  UserPermissions
} from '../@types/user';

/**
 * User Management Service Class
 * Handles all user-related operations for managers
 */
export class UserManagementService {
  private static readonly USERS_COLLECTION = 'users';
  private static readonly USER_ACTIVITY_COLLECTION = 'userActivity';
  private static readonly USER_SESSIONS_COLLECTION = 'userSessions';
  private static readonly AUDIT_LOG_COLLECTION = 'auditLogs';

  /**
   * Get a secondary Firebase Auth instance for user creation
   * This prevents the current manager from being signed out
   */
  private static getSecondaryAuth() {
    const secondaryAppName = 'SecondaryUserCreationApp';
    let secondaryApp;
    
    if (getApps().some(app => app.name === secondaryAppName)) {
      secondaryApp = getApp(secondaryAppName);
    } else {
      secondaryApp = initializeApp(firebaseConfig, secondaryAppName);
    }
    
    return getAuth(secondaryApp);
  }

  /**
   * Default permissions for each role
   */
  private static readonly DEFAULT_PERMISSIONS: Record<UserRole, UserPermissions> = {
    'manager': {
      canViewOrders: true,
      canCreateOrders: true,
      canEditOrders: true,
      canDeleteOrders: true,
      canViewStores: true,
      canEditStores: true,
      canManageUsers: true,
      canViewReports: true,
      canExportData: true,
      canManageInventory: true,
    },
    'hand-lead': {
      canViewOrders: true,
      canCreateOrders: true,
      canEditOrders: true,
      canDeleteOrders: true,
      canViewStores: true,
      canEditStores: true,
      canManageUsers: true,
      canViewReports: true,
      canExportData: true,
      canManageInventory: true,
    },
    'sales-representative': {
      canViewOrders: true,
      canCreateOrders: true,
      canEditOrders: true,
      canDeleteOrders: false,
      canViewStores: true,
      canEditStores: false,
      canManageUsers: false,
      canViewReports: false,
      canExportData: false,
      canManageInventory: false,
    },
    'admin': {
      canViewOrders: true,
      canCreateOrders: true,
      canEditOrders: true,
      canDeleteOrders: true,
      canViewStores: true,
      canEditStores: true,
      canManageUsers: true,
      canViewReports: true,
      canExportData: true,
      canManageInventory: true,
    }
  };

  /**
   * Get all users with filtering and sorting
   * 
   * @param filters - Optional filters to apply
   * @param sort - Optional sorting configuration
   * @param pageSize - Number of results per page (default: 50)
   * @returns Promise resolving to array of users
   * @throws Error if database operation fails
   */
  static async getUsers(
    filters?: UserFilters,
    sort?: UserSort,
    pageSize: number = 50
  ): Promise<{ users: UserProfile[]; hasMore: boolean }> {
    try {
      const usersRef = collection(db, this.USERS_COLLECTION);
      const constraints: QueryConstraint[] = [];

      // Apply filters
      if (filters) {
        // Role filter
        if (filters.role) {
          if (Array.isArray(filters.role)) {
            constraints.push(where('role', 'in', filters.role));
          } else {
            constraints.push(where('role', '==', filters.role));
          }
        }

        // Status filter
        if (filters.status) {
          if (Array.isArray(filters.status)) {
            constraints.push(where('status', 'in', filters.status));
          } else {
            constraints.push(where('status', '==', filters.status));
          }
        }

        // Territory filter
        if (filters.territory) {
          constraints.push(where('territory', '==', filters.territory));
        }

        // Region filter
        if (filters.region) {
          constraints.push(where('region', '==', filters.region));
        }

        // Department filter
        if (filters.department) {
          constraints.push(where('department', '==', filters.department));
        }

        // Manager filter
        if (filters.managerId) {
          constraints.push(where('managerId', '==', filters.managerId));
        }

        // Date filters
        if (filters.createdAfter) {
          constraints.push(where('createdAt', '>=', Timestamp.fromDate(filters.createdAfter)));
        }
        if (filters.createdBefore) {
          constraints.push(where('createdAt', '<=', Timestamp.fromDate(filters.createdBefore)));
        }

        // Active users filter
        if (filters.isActive !== undefined) {
          constraints.push(where('status', '==', filters.isActive ? 'active' : 'inactive'));
        }
      }

      // Apply sorting - only if no other filters are applied to avoid index requirements
      // Or if we're sorting by a field that has an index with the filters
      if (constraints.length === 0) {
        // No filters, safe to sort by any field
        const sortField = sort?.field || 'createdAt';
        const sortDirection = sort?.direction || 'desc';
      constraints.push(orderBy(sortField, sortDirection));
      }

      // Add limit
      constraints.push(limit(pageSize + 1));

      const q = query(usersRef, ...constraints);
      const querySnapshot = await getDocs(q);

      const users: UserProfile[] = [];
      const docs = querySnapshot.docs;
      const hasMore = docs.length > pageSize;

      // Process results (exclude the extra document if present)
      const docsToProcess = hasMore ? docs.slice(0, -1) : docs;

      for (const docSnap of docsToProcess) {
        const data = docSnap.data();
        users.push(this.formatUserData(docSnap.id, data));
      }

      // Apply client-side filters for complex queries
      let filteredUsers = users;

      if (filters) {
        // Search term filter
        if (filters.searchTerm) {
          const searchLower = filters.searchTerm.toLowerCase();
          filteredUsers = users.filter(user =>
            user.firstName?.toLowerCase().includes(searchLower) ||
            user.lastName?.toLowerCase().includes(searchLower) ||
            user.email?.toLowerCase().includes(searchLower) ||
            user.displayName?.toLowerCase().includes(searchLower) ||
            user.salesGroup?.toLowerCase().includes(searchLower)
          );
        }

        // Permission filter
        if (filters.hasPermission) {
          filteredUsers = filteredUsers.filter(user => 
            user.permissions?.[filters.hasPermission!]
          );
        }

        // Last login filters
        if (filters.lastLoginAfter) {
          filteredUsers = filteredUsers.filter(user => {
            if (!user.lastLoginAt) return false;
            const loginDate = user.lastLoginAt instanceof Date 
              ? user.lastLoginAt 
              : user.lastLoginAt.toDate();
            return loginDate >= filters.lastLoginAfter!;
          });
        }
        if (filters.lastLoginBefore) {
          filteredUsers = filteredUsers.filter(user => {
            if (!user.lastLoginAt) return false;
            const loginDate = user.lastLoginAt instanceof Date 
              ? user.lastLoginAt 
              : user.lastLoginAt.toDate();
            return loginDate <= filters.lastLoginBefore!;
          });
        }
      }

      // Apply client-side sorting (since we can't use orderBy with filters without indexes)
      if (sort) {
        const sortField = sort.field || 'firstName';
        const sortDirection = sort.direction || 'asc';
        
        filteredUsers.sort((a, b) => {
          const aValue = a[sortField as keyof UserProfile] || '';
          const bValue = b[sortField as keyof UserProfile] || '';
          
          let comparison = 0;
          if (aValue < bValue) comparison = -1;
          if (aValue > bValue) comparison = 1;
          
          return sortDirection === 'asc' ? comparison : -comparison;
        });
      }

      return {
        users: filteredUsers,
        hasMore
      };
    } catch (error: any) {
      console.error('Error fetching users:', error);
      throw new Error(`Failed to fetch users: ${error.message}`);
    }
  }

  /**
   * Get a single user by ID
   * 
   * @param userId - The user ID
   * @returns Promise resolving to the user data or null if not found
   * @throws Error if database operation fails
   */
  static async getUserById(userId: string): Promise<UserProfile | null> {
    try {
      if (!userId) {
        throw new Error('User ID is required');
      }

      const userRef = doc(db, this.USERS_COLLECTION, userId);
      const userSnap = await getDoc(userRef);

      if (!userSnap.exists()) {
        console.warn(`User not found: ${userId}`);
        return null;
      }

      const data = userSnap.data();
      return this.formatUserData(userId, data);
    } catch (error: any) {
      console.error('Error fetching user:', error);
      throw new Error(`Failed to fetch user: ${error.message}`);
    }
  }

  /**
   * Create a new user
   * 
   * @param userData - User creation data
   * @param createdBy - ID of the user creating this user
   * @returns Promise resolving to action result
   * @throws Error if creation fails
   */
  static async createUser(
    userData: CreateUserData,
    createdBy: string
  ): Promise<UserActionResult> {
    try {
      // Validate required fields
      if (!userData.email || !userData.firstName || !userData.lastName || !userData.role) {
        throw new Error('Email, first name, last name, and role are required');
      }

      // Check if user already exists
      const existingUser = await this.getUserByEmail(userData.email);
      if (existingUser) {
        throw new Error('User with this email already exists');
      }

      // Always create Firebase Auth user
      // Use provided temporary password or generate one
      const password = userData.temporaryPassword || this.generateTemporaryPassword();
      
      let firebaseUser: FirebaseUser | null = null;
      let userId: string;
      
      try {
        // Use secondary auth to prevent signing out the current manager
        const secondaryAuth = this.getSecondaryAuth();
        const credential = await createUserWithEmailAndPassword(
          secondaryAuth,
          userData.email,
          password
        );
        firebaseUser = credential.user;
        userId = firebaseUser.uid;

        // Update display name in Auth
        await updateProfile(firebaseUser, {
          displayName: `${userData.firstName} ${userData.lastName}`
        });
      } catch (authError: any) {
        console.error('Error creating Firebase Auth user:', authError);
        throw new Error(`Failed to create user in Firebase Authentication: ${authError.message}`);
      }

      // Get default permissions for role
      const defaultPermissions = this.DEFAULT_PERMISSIONS[userData.role];
      const permissions = { ...defaultPermissions, ...userData.permissions };

      // Determine sales groups - support both single and multiple
      const salesGroups = userData.salesGroups && userData.salesGroups.length > 0
        ? userData.salesGroups
        : userData.salesGroup
        ? [userData.salesGroup]
        : [];

      // Create user profile in Firestore using the Firebase Auth UID as document ID
      const userProfile: Omit<UserProfile, 'id'> = {
        email: userData.email,
        firstName: userData.firstName,
        lastName: userData.lastName,
        displayName: `${userData.firstName} ${userData.lastName}`,
        phoneNumber: userData.phoneNumber || '',
        role: userData.role,
        status: userData.status,
        permissions,
        territory: userData.territory || '',
        region: userData.region || '',
        department: userData.department || '',
        jobTitle: userData.jobTitle || '',
        managerId: userData.managerId || '',
        // Sales groups - store both for backward compatibility
        salesGroup: salesGroups.length === 1 ? salesGroups[0] : salesGroups[0] || undefined,
        ...(salesGroups.length > 0 && { salesGroups }), // Only include if not empty
        createdAt: serverTimestamp() as Timestamp,
        updatedAt: serverTimestamp() as Timestamp,
        failedLoginAttempts: 0,
        twoFactorEnabled: false,
        preferences: {
          language: 'en',
          timezone: 'UTC',
          notifications: {
            email: true,
            push: true,
            sms: false,
          },
          dashboard: {
            defaultView: 'overview',
            widgets: ['orders', 'stores', 'performance'],
          },
        },
      };

      try {
        // Use setDoc with the Firebase Auth UID as the document ID
        // This call will be performed using the main app (where the manager is still signed in)
        const userRef = doc(db, this.USERS_COLLECTION, userId);
        await setDoc(userRef, userProfile);
      } catch (firestoreError: any) {
        // If Firestore creation fails, clean up the Auth user
        console.error('Error creating Firestore user document:', firestoreError);
        if (firebaseUser) {
          try {
            // Delete the newly created Auth user since the profile creation failed
            // This is possible because firebaseUser belongs to the secondary app where it's currently signed in
            await deleteUser(firebaseUser);
            console.log(`Successfully cleaned up Auth user ${userId} after Firestore failure.`);
          } catch (cleanupError: any) {
            console.error('Error during cleanup:', cleanupError);
            console.warn(`⚠️ Auth user ${userId} created but Firestore document creation failed. Manual cleanup may be required. Error: ${cleanupError.message}`);
          }
        }
        throw new Error(`Failed to create user in Firestore: ${firestoreError.message}`);
      }

      // Log audit trail
      await this.logAuditEntry({
        userId: createdBy,
        action: 'createUser',
        entity: 'User',
        entityId: userId,
        changes: { 
          email: { old: null, new: userData.email },
          role: { old: null, new: userData.role }
        },
        notes: `Created user ${userData.firstName} ${userData.lastName}`,
        metadata: {
          createdUserEmail: userData.email,
          createdUserRole: userData.role,
          sendWelcomeEmail: userData.sendWelcomeEmail
        }
      });

      return {
        success: true,
        userId,
        message: 'User created successfully'
      };
    } catch (error: any) {
      console.error('Error creating user:', error);
      return {
        success: false,
        error: error.message || 'Failed to create user'
      };
    }
  }

  /**
   * Update an existing user
   * 
   * @param userId - The user ID
   * @param userData - User update data
   * @param updatedBy - ID of the user performing the update
   * @returns Promise resolving to action result
   * @throws Error if update fails
   */
  static async updateUser(
    userId: string,
    userData: UpdateUserData,
    updatedBy: string
  ): Promise<UserActionResult> {
    try {
      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/8fcaecbc-c806-4a7c-898a-36c3087877a6',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'UserManagementService.ts:479',message:'updateUser entry',data:{userId,hasPassword:!!userData.password,passwordLength:userData.password?.length,passwordValue:userData.password?userData.password.substring(0,2)+'***':null,userDataKeys:Object.keys(userData)},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'C'})}).catch(()=>{});
      // #endregion
      
      if (!userId) {
        throw new Error('User ID is required');
      }

      const userRef = doc(db, this.USERS_COLLECTION, userId);
      
      // Get current user data for audit trail
      const currentUser = await this.getUserById(userId);
      if (!currentUser) {
        throw new Error('User not found');
      }
      
      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/8fcaecbc-c806-4a7c-898a-36c3087877a6',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'UserManagementService.ts:495',message:'User retrieved for update',data:{userId,userEmail:currentUser.email,userDocId:currentUser.id},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'C'})}).catch(()=>{});
      // #endregion

      // Handle email update via Cloud Function (requires Admin SDK)
      if (userData.email && userData.email !== currentUser.email) {
        const functionName = 'updateUserEmail';
        // Always use the standard Firebase Functions URL format for updateUserEmail
        // (not Cloud Run service URL, which is used for other functions)
        const projectId = ENV.FIREBASE_PROJECT_ID || 'mckenzieportal-41c3d';
        const updateEmailFunctionUrl = `https://us-central1-${projectId}.cloudfunctions.net/${functionName}`;

        try {
          const response = await fetch(updateEmailFunctionUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            mode: 'cors',
            credentials: 'omit',
            body: JSON.stringify({ 
              userId: userId,
              newEmail: userData.email
            })
          });

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
            throw new Error(errorData.error || `Failed to update email: HTTP ${response.status}`);
          }
          
          console.log(`✅ User ${userId} email updated to ${userData.email}`);
        } catch (emailError: any) {
          console.error('Error updating email:', emailError);
          throw new Error(`Failed to update email: ${emailError.message}`);
        }
      }

      // Handle password update via Cloud Function (requires Admin SDK)
      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/8fcaecbc-c806-4a7c-898a-36c3087877a6',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'UserManagementService.ts:540',message:'Checking password field',data:{userId,hasPassword:!!userData.password,passwordLength:userData.password?.length,passwordTrimmedLength:userData.password?.trim().length,passwordValue:userData.password?userData.password.substring(0,2)+'***':null},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'B'})}).catch(()=>{});
      // #endregion
      
      if (userData.password && userData.password.trim().length > 0) {
        // #region agent log
        fetch('http://127.0.0.1:7242/ingest/8fcaecbc-c806-4a7c-898a-36c3087877a6',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'UserManagementService.ts:541',message:'Password update detected',data:{userId,passwordLength:userData.password.length,hasPassword:true},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'B'})}).catch(()=>{});
        // #endregion
        if (userData.password.length < 8) {
          throw new Error('Password must be at least 8 characters long');
        }

        const functionName = 'updateUserPassword';
        // Always use the standard Firebase Functions URL format for updateUserPassword
        // (not Cloud Run service URL, which is used for other functions)
        const projectId = ENV.FIREBASE_PROJECT_ID || 'mckenzieportal-41c3d';
        const updatePasswordFunctionUrl = `https://us-central1-${projectId}.cloudfunctions.net/${functionName}`;

        // #region agent log
        fetch('http://127.0.0.1:7242/ingest/8fcaecbc-c806-4a7c-898a-36c3087877a6',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'UserManagementService.ts:552',message:'Calling Cloud Function for password update',data:{userId,functionUrl:updatePasswordFunctionUrl,projectId},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'D'})}).catch(()=>{});
        // #endregion

        try {
          // #region agent log
          fetch('http://127.0.0.1:7242/ingest/8fcaecbc-c806-4a7c-898a-36c3087877a6',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'UserManagementService.ts:565',message:'About to call Cloud Function',data:{userId,requestBody:{userId,hasNewPassword:true,newPasswordLength:userData.password.length}},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'D'})}).catch(()=>{});
          // #endregion
          
          const response = await fetch(updatePasswordFunctionUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            mode: 'cors',
            credentials: 'omit',
            body: JSON.stringify({ 
              userId: userId,
              newPassword: userData.password
            })
          });

          // #region agent log
          fetch('http://127.0.0.1:7242/ingest/8fcaecbc-c806-4a7c-898a-36c3087877a6',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'UserManagementService.ts:578',message:'Cloud Function response received',data:{userId,status:response.status,statusText:response.statusText,ok:response.ok},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'D'})}).catch(()=>{});
          // #endregion

          if (!response.ok) {
            let errorData: any;
            let errorText: string = '';
            try {
              errorText = await response.text();
              errorData = JSON.parse(errorText);
            } catch (parseError) {
              errorData = { error: errorText || `HTTP ${response.status}` };
            }
            // #region agent log
            fetch('http://127.0.0.1:7242/ingest/8fcaecbc-c806-4a7c-898a-36c3087877a6',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'UserManagementService.ts:582',message:'Cloud Function error response',data:{userId,status:response.status,statusText:response.statusText,errorData,errorText:errorText.substring(0,500)},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'D'})}).catch(()=>{});
            // #endregion
            throw new Error(errorData.error || errorData.message || errorText || `Failed to update password: HTTP ${response.status}`);
          }
          
          // #region agent log
          const responseData = await response.json().catch(() => ({}));
          fetch('http://127.0.0.1:7242/ingest/8fcaecbc-c806-4a7c-898a-36c3087877a6',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'UserManagementService.ts:590',message:'Password update successful',data:{userId,responseData},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'D'})}).catch(()=>{});
          // #endregion
          console.log(`✅ User ${userId} password updated`);
        } catch (passwordError: any) {
          // #region agent log
          fetch('http://127.0.0.1:7242/ingest/8fcaecbc-c806-4a7c-898a-36c3087877a6',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'UserManagementService.ts:595',message:'Password update exception',data:{userId,errorMessage:passwordError.message,errorName:passwordError.name,errorStack:passwordError.stack?.substring(0,200)},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'D'})}).catch(()=>{});
          // #endregion
          console.error('Error updating password:', passwordError);
          throw new Error(`Failed to update password: ${passwordError.message}`);
        }
      } else {
        // #region agent log
        fetch('http://127.0.0.1:7242/ingest/8fcaecbc-c806-4a7c-898a-36c3087877a6',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'UserManagementService.ts:602',message:'No password in userData',data:{userId,hasPassword:!!userData.password,passwordValue:userData.password,passwordTrimmed:userData.password?.trim()},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'B'})}).catch(()=>{});
        // #endregion
      }

      // Prepare update data
      const updateData: any = {
        updatedAt: serverTimestamp(),
      };

      // Update fields if provided
      if (userData.firstName) updateData.firstName = userData.firstName;
      if (userData.lastName) updateData.lastName = userData.lastName;
      if (userData.displayName) updateData.displayName = userData.displayName;
      if (userData.phoneNumber !== undefined) updateData.phoneNumber = userData.phoneNumber;
      if (userData.role) updateData.role = userData.role;
      if (userData.status) updateData.status = userData.status;
      if (userData.territory !== undefined) updateData.territory = userData.territory;
      if (userData.department !== undefined) updateData.department = userData.department;
      if (userData.jobTitle !== undefined) updateData.jobTitle = userData.jobTitle;
      if (userData.managerId !== undefined) updateData.managerId = userData.managerId;
      if (userData.preferences) updateData.preferences = { ...currentUser.preferences, ...userData.preferences };
      
      // Update email in Firestore if it was changed
      if (userData.email && userData.email !== currentUser.email) {
        updateData.email = userData.email;
      }

      // Handle sales groups - support both single and multiple
      if (userData.salesGroups !== undefined) {
        if (userData.salesGroups.length > 0) {
          updateData.salesGroups = userData.salesGroups;
          updateData.salesGroup = userData.salesGroups.length === 1 ? userData.salesGroups[0] : userData.salesGroups[0]; // For backward compatibility
        } else {
          // If empty array, clear both
          updateData.salesGroups = [];
          updateData.salesGroup = null;
        }
      } else if (userData.salesGroup !== undefined) {
        // Handle single salesGroup for backward compatibility
        updateData.salesGroup = userData.salesGroup;
        updateData.salesGroups = [userData.salesGroup];
      }

      // Update permissions if provided
      if (userData.permissions) {
        updateData.permissions = { ...currentUser.permissions, ...userData.permissions };
      }

      // Update display name if first/last name changed
      if (userData.firstName || userData.lastName) {
        const newFirstName = userData.firstName || currentUser.firstName;
        const newLastName = userData.lastName || currentUser.lastName;
        updateData.displayName = `${newFirstName} ${newLastName}`;
      }

      // Handle store assignments
      if (userData.assignedStoreIds !== undefined && Array.isArray(userData.assignedStoreIds)) {
        updateData.assignedStoreIds = userData.assignedStoreIds;
        
        // Get current assigned stores
        const currentStoreIds = currentUser.assignedStoreIds || [];
        
        // Get username for assignment history
        const username = currentUser.email?.split('@')[0] || currentUser.displayName || '';
        
        // Find stores to add (new assignments)
        const storesToAdd = userData.assignedStoreIds.filter(
          storeId => !currentStoreIds.includes(storeId)
        );
        
        // Find stores to remove (unassignments)
        const storesToRemove = currentStoreIds.filter(
          storeId => !userData.assignedStoreIds!.includes(storeId)
        );
        
        // Process store assignments using StoreAssignmentService
        const assignmentService = new StoreAssignmentService();
        
        // Add new assignments
        for (const storeId of storesToAdd) {
          try {
            await assignmentService.assignStoreToUser(
              storeId,
              userId,
              username,
              updatedBy,
              'manual',
              `Assigned via user edit form`
            );
          } catch (error) {
            console.error(`Failed to assign store ${storeId} to user ${userId}:`, error);
            // Continue with other assignments even if one fails
          }
        }
        
        // Remove unassignments
        for (const storeId of storesToRemove) {
          try {
            await assignmentService.removeStoreAssignment(
              storeId,
              userId,
              updatedBy,
              'manual'
            );
          } catch (error) {
            console.error(`Failed to unassign store ${storeId} from user ${userId}:`, error);
            // Continue with other unassignments even if one fails
          }
        }
      }

      await updateDoc(userRef, updateData);

      // Log audit trail
      await this.logAuditEntry({
        userId: updatedBy,
        action: 'updateUser',
        entity: 'User',
        entityId: userId,
        changes: this.getChangesObject(currentUser, updateData),
        notes: `Updated user ${currentUser.displayName}`,
        targetUserId: userId
      });

      return {
        success: true,
        userId,
        message: 'User updated successfully'
      };
    } catch (error: any) {
      console.error('Error updating user:', error);
      return {
        success: false,
        error: error.message || 'Failed to update user'
      };
    }
  }

  /**
   * Delete a user from both Firebase Authentication and Firestore
   * 
   * @param userId - The user ID
   * @param deletedBy - ID of the user performing the deletion
   * @param reason - Reason for deletion (optional)
   * @returns Promise resolving to action result
   * @throws Error if deletion fails
   */
  static async deleteUser(
    userId: string,
    deletedBy: string,
    reason?: string
  ): Promise<UserActionResult> {
    try {
      if (!userId) {
        throw new Error('User ID is required');
      }

      // Get current user data before deletion
      const currentUser = await this.getUserById(userId);
      if (!currentUser) {
        throw new Error('User not found');
      }

      // Get Firebase Functions URL
      const functionName = 'deleteUser';
      // Always use the standard Firebase Functions URL format for deleteUser
      // (not Cloud Run service URL, which is used for other functions)
      const projectId = ENV.FIREBASE_PROJECT_ID || 'mckenzieportal-41c3d';
      const deleteUserFunctionUrl = `https://us-central1-${projectId}.cloudfunctions.net/${functionName}`;

      // Call Cloud Function to delete from Firebase Authentication
      try {
        const response = await fetch(deleteUserFunctionUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          mode: 'cors',
          credentials: 'omit',
          body: JSON.stringify({ userId })
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
          // If user not found in Auth, continue with Firestore deletion
          if (response.status === 404 && errorData.error?.includes('not found')) {
            console.warn(`⚠️ User ${userId} not found in Firebase Authentication, continuing with Firestore deletion`);
          } else {
            throw new Error(errorData.error || `Failed to delete user from Authentication: HTTP ${response.status}`);
          }
        } else {
          console.log(`✅ User ${userId} deleted from Firebase Authentication`);
        }
      } catch (authError: any) {
        // If function is not deployed or URL is incorrect, log warning but continue
        if (authError.message?.includes('404') || authError.message?.includes('Failed to fetch')) {
          console.warn(`⚠️ Could not delete user from Authentication (function may not be deployed): ${authError.message}`);
          console.warn('⚠️ Continuing with Firestore deletion only');
        } else {
          throw authError;
        }
      }

      // Delete from Firestore
      const userRef = doc(db, this.USERS_COLLECTION, userId);
      await deleteDoc(userRef);
      console.log(`✅ User ${userId} deleted from Firestore`);

      // Log audit trail
      await this.logAuditEntry({
        userId: deletedBy,
        action: 'deleteUser',
        entity: 'User',
        entityId: userId,
        changes: { status: { old: currentUser.status, new: 'deleted' } },
        notes: `Deleted user ${currentUser.displayName} from Authentication and Firestore`,
        targetUserId: userId,
        reason: reason || undefined
      });

      return {
        success: true,
        userId,
        message: 'User deleted successfully from Authentication and Firestore'
      };
    } catch (error: any) {
      console.error('Error deleting user:', error);
      return {
        success: false,
        error: error.message || 'Failed to delete user'
      };
    }
  }

  /**
   * Reset user password
   * 
   * @param userId - The user ID
   * @param resetBy - ID of the user performing the reset
   * @returns Promise resolving to action result
   * @throws Error if reset fails
   */
  static async resetUserPassword(
    userId: string,
    resetBy: string
  ): Promise<UserActionResult> {
    try {
      const user = await this.getUserById(userId);
      if (!user) {
        throw new Error('User not found');
      }

      // Send password reset email
      await sendPasswordResetEmail(auth, user.email);

      // Log audit trail
      await this.logAuditEntry({
        userId: resetBy,
        action: 'resetPassword',
        entity: 'User',
        entityId: userId,
        notes: `Password reset email sent to ${user.email}`,
        targetUserId: userId
      });

      return {
        success: true,
        userId,
        message: 'Password reset email sent successfully'
      };
    } catch (error: any) {
      console.error('Error resetting password:', error);
      return {
        success: false,
        error: error.message || 'Failed to reset password'
      };
    }
  }

  /**
   * Perform bulk action on multiple users
   * 
   * @param bulkAction - Bulk action configuration
   * @param performedBy - ID of the user performing the action
   * @returns Promise resolving to results for each user
   */
  static async performBulkAction(
    bulkAction: BulkUserAction,
    performedBy: string
  ): Promise<UserActionResult[]> {
    const results: UserActionResult[] = [];

    try {
      for (const userId of bulkAction.userIds) {
        try {
          let result: UserActionResult;

          switch (bulkAction.action) {
            case 'activate':
              result = await this.updateUser(
                userId,
                { status: 'active' },
                performedBy
              );
              break;

            case 'deactivate':
              result = await this.updateUser(
                userId,
                { status: 'inactive' },
                performedBy
              );
              break;

            case 'suspend':
              result = await this.updateUser(
                userId,
                { status: 'suspended' },
                performedBy
              );
              break;

            case 'delete':
              result = await this.deleteUser(
                userId,
                performedBy,
                bulkAction.reason
              );
              break;

            case 'changeRole':
              if (!bulkAction.newRole) {
                throw new Error('New role is required for changeRole action');
              }
              result = await this.updateUser(
                userId,
                { role: bulkAction.newRole },
                performedBy
              );
              break;

            case 'resetPassword':
              result = await this.resetUserPassword(userId, performedBy);
              break;

            case 'sendWelcomeEmail':
              // TODO: Implement welcome email functionality
                result = {
                success: true,
                  userId,
                message: 'Welcome email sent'
                };
              break;

            default:
              result = {
                success: false,
                userId,
                error: `Unsupported action: ${bulkAction.action}`
              };
          }

          results.push(result);
        } catch (error: any) {
          results.push({
            success: false,
            userId,
            error: error.message
          });
        }
      }

      return results;
    } catch (error: any) {
      console.error('Error performing bulk action:', error);
      throw new Error(`Bulk action failed: ${error.message}`);
    }
  }

  /**
   * Get user statistics for dashboard
   * 
   * @returns Promise resolving to user statistics
   */
  static async getUserStatistics(): Promise<UserStatistics> {
    try {
      const usersRef = collection(db, this.USERS_COLLECTION);
      const querySnapshot = await getDocs(usersRef);

      const stats: UserStatistics = {
        totalUsers: 0,
        activeUsers: 0,
        inactiveUsers: 0,
        suspendedUsers: 0,
        pendingUsers: 0,
        usersByRole: {
          'manager': 0,
          'sales-representative': 0,
          'admin': 0,
          'hand-lead': 0
        },
        usersByTerritory: {},
        recentSignups: 0,
        usersOnline: 0,
        averageSessionDuration: 0,
        topPerformers: []
      };

      const now = new Date();
      const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

      querySnapshot.forEach((doc) => {
        const user = doc.data();
        const createdAt = user.createdAt?.toDate?.() || user.createdAt;
        const lastActivity = user.lastActivityAt?.toDate?.() || user.lastActivityAt;

        stats.totalUsers++;

        // Count by status
        switch (user.status) {
          case 'active':
            stats.activeUsers++;
            break;
          case 'inactive':
            stats.inactiveUsers++;
            break;
          case 'suspended':
            stats.suspendedUsers++;
            break;
          case 'pending':
            stats.pendingUsers++;
            break;
        }

        // Count by role
        const userRole = user.role as UserRole | undefined;
        if (userRole && userRole in stats.usersByRole) {
          stats.usersByRole[userRole]++;
        }

        // Count by territory
        if (user.territory) {
          stats.usersByTerritory[user.territory] = 
            (stats.usersByTerritory[user.territory] || 0) + 1;
        }

        // Recent signups
        if (createdAt && createdAt >= thirtyDaysAgo) {
          stats.recentSignups++;
        }

        // Users online (active in last 15 minutes)
        if (lastActivity && lastActivity >= new Date(now.getTime() - 15 * 60 * 1000)) {
          stats.usersOnline++;
        }
      });

      return stats;
    } catch (error: any) {
      console.error('Error calculating user statistics:', error);
      throw new Error(`Failed to calculate user statistics: ${error.message}`);
    }
  }

  /**
   * Get user by email
   * 
   * @param email - User email
   * @returns Promise resolving to user or null
   * @private
   */
  private static async getUserByEmail(email: string): Promise<UserProfile | null> {
    try {
      const usersRef = collection(db, this.USERS_COLLECTION);
      const q = query(usersRef, where('email', '==', email), limit(1));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        return null;
      }

      const doc = querySnapshot.docs[0];
      const data = doc.data();
      return this.formatUserData(doc.id, data);
    } catch (error: any) {
      console.error('Error finding user by email:', error);
      return null;
    }
  }

  /**
   * Format user data from Firestore document
   * 
   * @param userId - The user ID
   * @param data - Raw Firestore data
   * @returns Formatted UserProfile object
   * @private
   */
  private static formatUserData(userId: string, data: any): UserProfile {
    // Generate firstName, lastName, and displayName from available data
    const firstName = data.firstName || data.username?.split('.')[0] || data.salesGroup || 'User';
    const lastName = data.lastName || data.username?.split('.')[1] || '';
    const displayName = data.displayName || 
                        (firstName && lastName ? `${firstName} ${lastName}`.trim() : firstName) ||
                        data.username || 
                        data.email?.split('@')[0] || 
                        'Unknown User';
    
    // Ensure status field exists
    const status = data.status || (data.lastLoginAt ? 'active' : 'pending');
    
    return {
      id: userId,
      ...data,
      firstName,
      lastName,
      displayName,
      status,
      // Convert Firestore Timestamps to Dates
      createdAt: data.createdAt?.toDate?.() || data.createdAt || new Date(),
      updatedAt: data.updatedAt?.toDate?.() || data.updatedAt,
      lastLoginAt: data.lastLoginAt?.toDate?.() || data.lastLoginAt,
      lastActivityAt: data.lastActivityAt?.toDate?.() || data.lastActivityAt,
      hireDate: data.hireDate?.toDate?.() || data.hireDate,
      passwordLastChanged: data.passwordLastChanged?.toDate?.() || data.passwordLastChanged,
      lockedUntil: data.lockedUntil?.toDate?.() || data.lockedUntil,
    } as UserProfile;
  }

  /**
   * Generate temporary password
   * 
   * @returns Temporary password string
   * @private
   */
  private static generateTemporaryPassword(): string {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return password;
  }

  /**
   * Get changes object for audit trail
   * 
   * @param oldData - Previous user data
   * @param newData - Updated user data
   * @returns Changes object
   * @private
   */
  private static getChangesObject(oldData: UserProfile, newData: any): Record<string, { old: any; new: any }> {
    const changes: Record<string, { old: any; new: any }> = {};
    
    Object.keys(newData).forEach(key => {
      if (key !== 'updatedAt' && oldData[key as keyof UserProfile] !== newData[key]) {
        changes[key] = {
          old: oldData[key as keyof UserProfile],
          new: newData[key]
        };
      }
    });

    return changes;
  }

  /**
   * Log audit entry for user actions
   * 
   * @param entry - Audit log entry data
   * @private
   */
  private static async logAuditEntry(entry: {
    userId: string;
    action: string;
    entity: string;
    entityId: string;
    changes?: Record<string, any>;
    notes?: string;
    targetUserId?: string;
    reason?: string;
    metadata?: Record<string, any>;
  }): Promise<void> {
    try {
      const auditRef = collection(db, this.AUDIT_LOG_COLLECTION);
      await addDoc(auditRef, {
        ...entry,
        timestamp: serverTimestamp()
      });
    } catch (error: any) {
      console.warn('Failed to log audit entry:', error.message);
      // Don't throw - audit logging is non-critical
    }
  }
}